/*
 * Quizz La Sobrerie, moteur de recommandation.
 *
 * Le parcours pose des questions ; chaque réponse devient une règle sur les
 * métachamps des produits (catégorie, type, style, régimes, alcool, adjectifs,
 * accords, contre-indications, niveaux 1 à 5). Aucun tag à poser : les fiches
 * telles qu'elles sont importées suffisent.
 *
 * Les produits viennent de la vue /collections/all?view=sommelier, la même que
 * le sommelier, chargée dès l'ouverture : prix, devise, URL et disponibilité
 * du marché du visiteur.
 *
 * Deux sortes de règles sur une réponse :
 *   - un filtre strict : fam (rayon), usage, style (bière), contrainte ;
 *   - des points : acc (accords, voisins à demi-point), adj (adjectifs),
 *     niv (niveau 1 à 5 à demi-point), styleBonus.
 * Le pourcentage affiché est la part des envies cochées que la bouteille
 * satisfait.
 */

if (typeof SobrerieQuizz !== 'function') {

  /* ------------------------------------------------------------------ *
   * L'arbre des questions
   * ------------------------------------------------------------------ */

  const ETAPES = [
    {
      id: 'contexte',
      eyebrow: 'Pour commencer',
      titre: 'Parlons un peu de vous',
      taille: 'large',
      groupes: [
        {
          id: 'destinataire',
          type: 'unique',
          label: "Cette bouteille, c'est...",
          options: [
            { v: 'moi', l: 'Pour moi', h: 'Je me fais plaisir' },
            { v: 'cadeau', l: 'Pour offrir', h: "C'est un cadeau" }
          ]
        },
        {
          id: 'quantite',
          type: 'unique',
          label: 'Vous cherchez...',
          options: [
            { v: 'une', l: 'Une seule bouteille', h: 'Une belle trouvaille' },
            { v: 'plusieurs', l: 'Plusieurs bouteilles', h: 'Un coffret, un lot, une petite cave' }
          ]
        }
      ]
    },

    {
      id: 'famille',
      eyebrow: 'Le rayon',
      titre: 'Quel type de boisson cherchez-vous ?',
      taille: 'large',
      groupes: [
        {
          id: 'famille',
          type: 'unique',
          options: [
            { v: 'gastronomiques', l: 'Boissons gastronomiques', h: 'À déguster comme un vin, à table', fam: 'gastro' },
            { v: 'vins', l: 'Vins désalcoolisés', h: 'Tranquilles, pétillants, rosés', fam: 'vins' },
            { v: 'spiritueux', l: 'Spiritueux et bases cocktail', h: 'Pour mixer ou boire sur glace', fam: 'spiritueux' },
            { v: 'biere', l: 'Bière sans alcool', h: 'Houblonnée, fraîche, désaltérante', fam: 'biere' }
          ]
        }
      ]
    },

    {
      id: 'accord',
      eyebrow: 'À table',
      titre: 'Pour quel accord ou quelle occasion ?',
      aide: 'Plusieurs réponses possibles.',
      taille: 'compact',
      si: (r) => estCoche(r.famille, 'gastronomiques') || estCoche(r.famille, 'vins'),
      groupes: [
        {
          id: 'accord',
          type: 'multiple',
          minimum: 1,
          options: [
            { v: 'apero', l: 'Apéro', acc: ['Apéritif'] },
            { v: 'mer', l: 'Poisson ou fruits de mer', acc: ['Poisson', 'Poisson cru', 'Fruits de mer', 'Huîtres'] },
            { v: 'volaille', l: 'Volaille', acc: ['Volailles', 'Canard'] },
            { v: 'viande-blanche', l: 'Viande blanche', acc: ['Viandes blanches'] },
            { v: 'viande-rouge', l: 'Viande rouge ou BBQ', acc: ['Viande rouge', 'Gibier'] },
            { v: 'mijote', l: 'Plat mijoté', acc: ['Plats mijotés', 'Légumes mijotés'] },
            { v: 'epice', l: 'Plat épicé', acc: ['Plats épicés', 'Cuisine asiatique', 'Cuisine levantine'] },
            { v: 'vegetal', l: 'Cuisine végétale', acc: ['Cuisine végétale', 'Salades fraîches', 'Légumes grillés', 'Légumes racines', 'Crudités', 'Plats forestiers', 'Risotto'] },
            { v: 'fromage', l: 'Fromage', acc: ['Fromages de Brebis ou Chèvre', 'Fromages frais', 'Fromages crémeux', 'Fromages à pâte dure', 'Fromages à pâte persillée', 'Fromages affinés'] },
            { v: 'fromage-fondu', l: 'Fromage fondu', acc: ['Fromage fondu'] },
            { v: 'dessert-chocolat', l: 'Dessert chocolaté', acc: ['Desserts chocolatés', 'Desserts aux fruits secs'] },
            { v: 'dessert-fruite', l: 'Dessert floral ou fruité', acc: ['Desserts aux fruits rouges', 'Desserts aux agrumes', 'Desserts aux fruits jaunes', 'Desserts floraux', 'Desserts à la pomme'] },
            { v: 'plaisir', l: 'Juste pour me faire plaisir', x: true }
          ]
        }
      ]
    },

    {
      id: 'usage',
      eyebrow: 'Les spiritueux',
      titre: 'Vous comptez la boire comment ?',
      taille: 'large',
      si: (r) => estCoche(r.famille, 'spiritueux'),
      groupes: [
        {
          id: 'usage',
          type: 'unique',
          options: [
            { v: 'cocktail', l: 'En cocktail', h: 'Allongée, mixée, secouée', usage: 'cocktail' },
            { v: 'glace', l: 'Sur glace', h: 'En apéritif ou en digestif', usage: 'glace' }
          ]
        }
      ]
    },

    {
      id: 'cocktail',
      eyebrow: 'Les spiritueux',
      titre: 'Quels cocktails aimez-vous ?',
      aide: 'Plusieurs réponses possibles.',
      taille: 'compact',
      si: (r) => r.usage === 'cocktail',
      groupes: [
        {
          id: 'cocktail',
          type: 'multiple',
          minimum: 1,
          options: [
            { v: 'spritz', l: 'Spritz', adj: ['Amer', 'Fruité'] },
            { v: 'rhum', l: 'À base de rhum', h: 'Mojito, daiquiri, punch', adj: ['Fruité', 'Acidulé'] },
            { v: 'hugo', l: 'Hugo, St-Germain', adj: ['Floral', 'Acidulé'] },
            { v: 'mule', l: 'Mule', adj: ['Épicé', 'Acidulé'] },
            { v: 'bellini', l: 'Bellini', adj: ['Fruité', 'Acidulé'] },
            { v: 'negroni', l: 'Negroni', adj: ['Amer'] },
            { v: 'gin-tonic', l: 'Gin tonic', adj: ['Sec', 'Herbacé'], niv: ['douceur', 1, 2] },
            { v: 'pastis', l: 'Pastis', adj: ['Anisé'], styleBonus: ['Anisé'] },
            { v: 'epice', l: 'Un cocktail épicé', adj: ['Épicé'] },
            { v: 'fruite', l: 'Un cocktail fruité', adj: ['Fruité'] },
            { v: 'amer', l: 'Un cocktail amer', adj: ['Amer'] },
            { v: 'acidule', l: 'Un cocktail acidulé', adj: ['Acidulé'] },
            { v: 'herbace', l: 'Un cocktail herbacé', adj: ['Herbacé'] }
          ]
        }
      ]
    },

    {
      id: 'profil_glace',
      eyebrow: 'Les spiritueux',
      titre: 'Quels profils préférez-vous ?',
      aide: 'Plusieurs réponses possibles.',
      taille: 'compact',
      si: (r) => r.usage === 'glace',
      groupes: [
        {
          id: 'profil_glace',
          type: 'multiple',
          minimum: 1,
          options: [
            { v: 'herbace', l: 'Herbacé', adj: ['Herbacé'] },
            { v: 'sec', l: 'Sec', adj: ['Sec'], niv: ['douceur', 1, 2] },
            { v: 'amer', l: 'Amer', adj: ['Amer'] },
            { v: 'acidule', l: 'Acidulé', adj: ['Acidulé'] },
            { v: 'fruite', l: 'Fruité', adj: ['Fruité'] },
            { v: 'amande', l: 'Amande', adj: ['Gourmand', 'Suave'] }
          ]
        }
      ]
    },

    {
      id: 'biere',
      eyebrow: 'La bière',
      titre: 'Quel type de bière souhaitez-vous ?',
      aide: 'Plusieurs réponses possibles.',
      taille: 'compact',
      si: (r) => estCoche(r.famille, 'biere'),
      groupes: [
        {
          id: 'biere',
          type: 'multiple',
          minimum: 1,
          options: [
            { v: 'ipa', l: 'IPA', h: 'Houblonnée, amère', style: 'IPA' },
            { v: 'blonde', l: 'Blonde', h: 'Ronde, facile', style: 'Blonde' },
            { v: 'ambree', l: 'Ambrée', h: 'Maltée, caramel', style: 'Ambrée' },
            { v: 'sour', l: 'Sour', h: 'Acidulée, vive', style: 'Sour' },
            { v: 'kriek', l: 'Kriek', h: 'Fruitée, cerise', style: 'Kriek' },
            { v: 'peu-importe', l: 'Peu importe, surprenez-moi', x: true }
          ]
        }
      ]
    },

    {
      id: 'profil',
      eyebrow: 'Le goût',
      titre: 'Vers quels profils allez-vous naturellement ?',
      aide: 'Plusieurs réponses possibles. Choisissez les familles qui vous parlent.',
      taille: 'large',
      si: (r) => estCoche(r.famille, 'gastronomiques') || estCoche(r.famille, 'vins'),
      groupes: [
        {
          id: 'profil',
          type: 'multiple',
          minimum: 1,
          options: [
            { v: 'vif', l: 'Frais et vif', h: 'Sec, minéral, tranchant', adj: ['Vif', 'Sec', 'Minéral', 'Frais'], niv: ['rondeur', 1, 2] },
            { v: 'fruite', l: 'Fruité et acidulé', h: 'Zesté, juteux, solaire', adj: ['Fruité', 'Acidulé', 'Hespéridé', 'Agrumes'] },
            { v: 'floral', l: 'Floral et délicat', h: 'Léger, parfumé, subtil', adj: ['Floral', 'Délicat', 'Subtil'], niv: ['puissance', 1, 2] },
            { v: 'herbace', l: 'Herbacé et végétal', h: 'Amer, sauvage, botanique', adj: ['Herbacé', 'Amer', 'Végétal'] },
            { v: 'gourmand', l: 'Rond et gourmand', h: 'Moelleux, ample, généreux', adj: ['Gourmand', 'Rond', 'Suave'], niv: ['douceur', 4, 5] },
            { v: 'structure', l: 'Structuré et boisé', h: 'Tannique, puissant, profond', adj: ['Structuré', 'Boisé', 'Tannique', 'Profond', 'Puissant'], niv: ['tannique', 4, 5] },
            { v: 'epice', l: 'Épicé et fumé', h: 'Poivré, torréfié, intense', adj: ['Épicé', 'Fumé', 'Chaud'] },
            { v: 'ouvert', l: 'Je me laisse guider', h: 'Surprenez-moi', x: true }
          ]
        }
      ]
    },

    {
      id: 'contraintes',
      eyebrow: 'Dernier point',
      titre: 'Une contrainte particulière ?',
      aide: 'Facultatif. Vous pourrez encore ajuster sur vos résultats.',
      taille: 'compact',
      facultative: true,
      groupes: [
        {
          id: 'contraintes',
          type: 'multiple',
          options: [
            { v: 'vegan', l: 'Vegan', contrainte: 'vegan' },
            { v: 'sans-gluten', l: 'Sans gluten', contrainte: 'gluten' },
            { v: 'grossesse', l: 'Compatible grossesse', contrainte: 'enceinte' },
            { v: 'non-petillant', l: 'Non pétillant', contrainte: 'tranquille' },
            { v: 'aucune', l: 'Aucune contrainte', x: true }
          ]
        }
      ]
    }
  ];

  /* Collections à explorer après les résultats, selon les réponses (clé « groupe:valeur »).
     Seules les collections qui existent en boutique sont listées ; quatre liens au plus. */
  const COLLECTIONS = {
    'famille:gastronomiques': ['boissons-de-degustation-atypiques', 'Boissons gastronomiques'],
    'famille:vins': ['vins-tranquilles-desalcoolises-convaincants', 'Vins tranquilles'],
    'famille:vins+bulles': ['vins-petillants-desalcoolises-convaincants', 'Vins pétillants'],
    'famille:spiritueux': ['aperitifs-raffines', 'Apéritifs et bases cocktail'],
    'famille:biere': ['nos-bieres-sans-alcool', 'Bières sans alcool'],
    'accord:apero': ['aperitifs-raffines', 'Apéritifs raffinés'],
    'accord:mer': ['les-accords-mer', 'Accords mer'],
    'accord:volaille': ['les-accords-terre', 'Accords terre'],
    'accord:viande-blanche': ['les-accords-terre', 'Accords terre'],
    'accord:viande-rouge': ['les-accords-terre', 'Accords terre'],
    'accord:mijote': ['les-accords-terre', 'Accords terre'],
    'accord:vegetal': ['les-accords-vegetaux', 'Accords végétaux'],
    'accord:fromage': ['les-accords-avec-le-fromage', 'Accords fromage'],
    'accord:fromage-fondu': ['les-boissons-gastronomiques-avec-le-fromage-fondu', 'Fromage fondu'],
    'accord:dessert-chocolat': ['les-accords-avec-les-desserts', 'Accords desserts'],
    'accord:dessert-fruite': ['les-accords-avec-les-desserts', 'Accords desserts'],
    'cocktail:spritz': ['landing-mixologie-amer', 'Cocktails amers'],
    'cocktail:negroni': ['landing-mixologie-amer', 'Cocktails amers'],
    'cocktail:amer': ['landing-mixologie-amer', 'Cocktails amers'],
    'cocktail:rhum': ['landing-mixologie-fruite', 'Cocktails fruités'],
    'cocktail:bellini': ['landing-mixologie-fruite', 'Cocktails fruités'],
    'cocktail:fruite': ['landing-mixologie-fruite', 'Cocktails fruités'],
    'cocktail:gin-tonic': ['landing-mixologie-herbace', 'Cocktails herbacés'],
    'cocktail:herbace': ['landing-mixologie-herbace', 'Cocktails herbacés'],
    'quantite:plusieurs': ['les-coffrets-decouvertes', 'Coffrets découvertes'],
    'destinataire:cadeau': ['les-coffrets-decouvertes', 'Coffrets découvertes']
  };

  /* Voisinage de rayon : quand un rayon n'a rien de disponible, ce vers quoi on élargit,
     avec ce qui fait remonter les bouteilles les plus proches. */
  const VOISINS_RAYON = {
    biere: { rayons: ['gastro'], adj: ['Amer', 'Herbacé', 'Houblonné'], libelle: 'nos boissons gastronomiques amères ou herbacées' },
    spiritueux: { rayons: ['gastro'], acc: ['Apéritif'], libelle: 'nos boissons gastronomiques d\'apéritif' },
    vins: { rayons: ['gastro'], libelle: 'nos boissons gastronomiques' },
    gastro: { rayons: ['vins'], libelle: 'nos vins désalcoolisés' }
  };
  const NOM_RAYON = { gastro: 'boissons gastronomiques', vins: 'vins désalcoolisés', spiritueux: 'spiritueux et bases cocktail', biere: 'bières' };

  /* Poids des questions qui donnent des points. */
  const POIDS = { accord: 3, profil: 3, cocktail: 3, profil_glace: 2 };

  /* Questions dont les points s'appliquent selon le rayon du produit. */
  const PORTEE = {
    accord: ['gastro', 'vins', 'lot'],
    profil: ['gastro', 'vins', 'lot'],
    cocktail: ['spiritueux', 'lot'],
    profil_glace: ['spiritueux', 'lot']
  };

  /* Corrélation voisine des accords, la même que le sommelier. */
  const VOISINS = {
    'Poisson': ['Poisson cru', 'Fruits de mer', 'Fritures'], 'Poisson cru': ['Poisson', 'Fruits de mer', 'Cuisine asiatique'],
    'Fruits de mer': ['Huîtres', 'Poisson', 'Poisson cru'], 'Huîtres': ['Fruits de mer', 'Poisson cru'], 'Fritures': ['Poisson', 'Apéritif'],
    'Viande rouge': ['Gibier', 'Plats mijotés', 'Charcuterie'], 'Viandes blanches': ['Volailles', 'Canard'], 'Volailles': ['Viandes blanches', 'Canard'],
    'Canard': ['Volailles', 'Gibier', 'Viande rouge'], 'Gibier': ['Viande rouge', 'Plats forestiers', 'Plats mijotés'],
    'Charcuterie': ['Apéritif', 'Fromages à pâte dure'], 'Foie gras': ['Sucré-salé', 'Charcuterie', 'Desserts aux fruits jaunes'],
    'Plats mijotés': ['Viande rouge', 'Légumes mijotés', 'Cuisine levantine'],
    'Salades fraîches': ['Crudités', 'Cuisine végétale', 'Légumes grillés'], 'Crudités': ['Salades fraîches', 'Cuisine végétale'],
    'Légumes grillés': ['Cuisine végétale', 'Légumes racines', 'Cuisine levantine'], 'Légumes racines': ['Légumes grillés', 'Plats forestiers', 'Légumes mijotés'],
    'Légumes mijotés': ['Plats mijotés', 'Légumes racines'], 'Plats forestiers': ['Risotto', 'Gibier', 'Légumes racines'],
    'Risotto': ['Plats forestiers', 'Fromages à pâte dure'], 'Cuisine végétale': ['Légumes grillés', 'Salades fraîches'],
    'Fromages de Brebis ou Chèvre': ['Fromages frais', 'Fromages crémeux'], 'Fromages frais': ['Fromages de Brebis ou Chèvre', 'Salades fraîches'],
    'Fromages crémeux': ['Fromages de Brebis ou Chèvre', 'Fromage fondu'], 'Fromages à pâte dure': ['Fromage fondu', 'Fromages affinés'],
    'Fromages à pâte persillée': ['Fromages affinés', 'Desserts aux fruits secs'], 'Fromages affinés': ['Fromages à pâte persillée', 'Fromages à pâte dure'],
    'Fromage fondu': ['Fromages à pâte dure', 'Fromages crémeux'],
    'Desserts chocolatés': ['Desserts aux fruits secs', 'Desserts aux fruits rouges'], 'Desserts aux fruits rouges': ['Desserts aux fruits jaunes', 'Desserts floraux'],
    'Desserts aux agrumes': ['Desserts aux fruits jaunes', 'Desserts floraux'], 'Desserts aux fruits jaunes': ['Desserts à la pomme', 'Desserts aux agrumes'],
    'Desserts à la pomme': ['Desserts aux fruits jaunes', 'Desserts aux fruits secs'], 'Desserts aux fruits secs': ['Desserts chocolatés', 'Desserts à la pomme'],
    'Desserts floraux': ['Desserts aux agrumes', 'Desserts aux fruits rouges'],
    'Apéritif': ['Charcuterie', 'Brunch'], 'Brunch': ['Apéritif', 'Desserts aux fruits rouges'], 'Sucré-salé': ['Foie gras', 'Cuisine asiatique'], 'Digestif': ['Desserts chocolatés'],
    'Cuisine asiatique': ['Plats épicés', 'Poisson cru'], 'Cuisine levantine': ['Plats épicés', 'Légumes grillés', 'Plats mijotés'],
    'Plats épicés': ['Cuisine asiatique', 'Cuisine levantine'], 'Plats à base de sauce tomate': ['Plats mijotés', 'Viande rouge']
  };

  /* Nettoyage du métachamp accords, le même que le sommelier. */
  const MIXERS = ['Tonic', 'Ginger Beer/Ale', 'Ginger Beer', 'Jus de cranberry', 'Jus de raisin', 'Jus de poire', "Jus d'orange", 'Jus de pamplemousse', 'Eau', 'Menthe', 'Orgeat', 'Sur Glace'];
  const CANON = { 'Volailles roties': 'Volailles', 'Huitres': 'Huîtres', 'Salades fraiches': 'Salades fraîches', 'Fromages affinés (type époisse)': 'Fromages affinés' };
  const EXCLURE_CATS = ['LDT Gift Option', 'Gift Option'];
  const LOTS = ['Lot', 'Pack'];

  const TEXTES = {
    suivant: 'Continuer',
    retour: 'Retour',
    passer: 'Passer cette question',
    commencer: 'Commencer le quizz',
    recommencer: 'Recommencer le quizz',
    etape: 'Étape',
    sur: 'sur',
    choisirUn: 'Choisissez au moins une réponse pour continuer.',
    voirProduit: 'Voir le produit',
    ajouter: 'Ajouter au panier',
    ajouterTout: 'Ajouter toute la sélection au panier',
    ajoutEnCours: 'Ajout…',
    ajoute: 'Ajouté',
    erreurPanier: "L'ajout au panier n'a pas abouti. Réessayez.",
    chargement: 'On parcourt la cave…',
    erreurCave: "La cave n'a pas pu être chargée.",
    emailInvalide: 'Merci de saisir une adresse email valide.',
    consentementRequis: 'Cochez la case pour vous inscrire, ou passez directement à vos résultats.',
    inscrit: "C'est noté, merci : vous recevrez nos nouveautés.",
    modifier: 'Modifier vos réponses',
    vousAvezChoisi: 'Vous avez choisi',
    explorer: 'Explorer les collections',
    epuise: 'épuisé',
    rienTitre: "On n'a rien d'assez juste pour vous aujourd'hui",
    rienTexte: 'Vos réponses se croisent sur un rayon où il ne reste rien en stock. Élargissez d\'un cran, ou dites-nous ce que vous mangez.',
    retirer: 'Retirer « {x} »',
    toutesBieres: 'toutes nos bières',
    rienStyle: 'Aucune bière {x} disponible en ce moment : voici {y}.',
    rienUsage: 'Rien pour « {x} » en ce moment : voici nos spiritueux et bases cocktail.',
    rienRayon: 'Rien de disponible côté {x} en ce moment : voici {y}.',
    sommelierLien: 'Demander au sommelier',
    reessayer: 'Réessayer',
    aucun: 'Aucune bouteille ne correspond à toutes vos réponses. Modifiez une réponse pour élargir.',
    peu: 'Seules {n} bouteilles correspondent à toutes vos réponses : on préfère vous les montrer que d\'en ajouter qui ne conviendraient pas.',
    peuUne: 'Une seule bouteille correspond à toutes vos réponses : on préfère vous la montrer que d\'en ajouter qui ne conviendraient pas.',
    autreIdee: 'Autre idée du rayon'
  };

  /* Accepte une réponse multiple (tableau) comme une réponse unique (texte). */
  function estCoche(valeur, cible) {
    return Array.isArray(valeur) ? valeur.indexOf(cible) !== -1 : valeur === cible;
  }

  function echapper(texte) {
    return String(texte == null ? '' : texte)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Formate un montant en centimes selon le format monétaire de la boutique
     (« CHF {{amount}} », « €{{amount_with_comma_separator}} », etc.). */
  function formaterMontant(centimes, format) {
    const valeur = centimes / 100;
    const separer = (entier, sep) => entier.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const [entier, decimales] = valeur.toFixed(2).split('.');
    const variantes = {
      amount: separer(entier, ',') + '.' + decimales,
      amount_no_decimals: separer(entier, ','),
      amount_with_comma_separator: separer(entier, '.') + ',' + decimales,
      amount_no_decimals_with_comma_separator: separer(entier, '.'),
      amount_with_apostrophe_separator: separer(entier, "'") + '.' + decimales,
      amount_no_decimals_with_space_separator: separer(entier, ' '),
      amount_with_space_separator: separer(entier, ' ') + ',' + decimales
    };
    return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, cle) => variantes[cle] != null ? variantes[cle] : variantes.amount);
  }

  /* Le producteur est déjà sur sa ligne : on l'enlève du titre quand le titre commence par lui. */
  function titreCourt(p) {
    const t = String(p.titre || '').trim();
    const v = String(p.vendeur || '').trim();
    if (v && t.toLowerCase().indexOf(v.toLowerCase()) === 0) {
      /* Seulement si le séparateur suit directement le producteur : « Nivers 2 - … » reste entier. */
      const m = t.slice(v.length).match(/^\s*[-–:]\s*(.+)$/);
      if (m && m[1].trim()) return m[1].trim();
    }
    return t;
  }

  /* La vue rend 250 produits par page, le maximum de Shopify, et annonce son nombre de pages :
     une fois la première arrivée, les suivantes sont demandées ensemble. Un produit déplacé
     d'une page à l'autre pendant le chargement n'est compté qu'une fois. */
  function chargerVue(url) {
    const page = (n) => {
      const adresse = new URL(url, window.location.origin);
      if (n > 1) adresse.searchParams.set('page', String(n));
      return fetch(adresse.toString(), { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); });
    };
    return page(1).then((premiere) => {
      const suivantes = [];
      for (let n = 2; n <= (Number(premiere.pages) || 1); n++) suivantes.push(page(n));
      return Promise.all(suivantes).then((autres) => {
        const vus = {};
        return [premiere].concat(autres)
          .reduce((tous, data) => tous.concat(data.produits || []), [])
          .filter((p) => { if (vus[p.h]) return false; vus[p.h] = true; return true; });
      });
    });
  }

  /* Rayon d'un produit, à partir de sa catégorie et de son type. */
  function rayon(p) {
    if (LOTS.indexOf(p.cat) !== -1) return 'lot';
    if (p.cat === 'Bière') return 'biere';
    if (p.cat === 'Vin Désalcoolisé') return 'vins';
    if (p.cat === 'Spiritueux' || p.cat === 'Cocktail' || p.type === 'Base pour cocktail') return 'spiritueux';
    return 'gastro';
  }

  /* Un métachamp liste arrive en tableau ; s'il devenait un texte simple, on ne plante pas. */
  function liste(x) {
    if (Array.isArray(x)) return x;
    return (x == null || x === '') ? [] : [x];
  }

  function normaliserProduit(brut) {
    const acc = [];
    liste(brut.acc).forEach((v) => {
      v = String(v).trim();
      if (MIXERS.indexOf(v) !== -1 || v.indexOf(' - ') !== -1 || v === 'Maison Giulia') return;
      v = CANON[v] || v;
      if (acc.indexOf(v) === -1) acc.push(v);
    });
    const niv = {};
    Object.keys(brut.niv || {}).forEach((k) => {
      if (brut.niv[k] != null && brut.niv[k] !== '') niv[k] = Number(brut.niv[k]);
    });
    const p = {
      handle: brut.h, titre: brut.t, vendeur: brut.v || '', url: brut.u, vid: brut.vid,
      cat: brut.cat || '', dispo: !!brut.dispo, coffret: !!brut.coffret, prix: brut.prix || '', prixNum: Number(brut.prix_num) || 0, image: brut.img || '',
      type: brut.type || '', style: brut.style || '', alcool: brut.alcool || '',
      reg: liste(brut.reg), adj: liste(brut.adj).map((a) => String(a).trim()), acc: acc,
      ci: liste(brut.ci).map((c) => String(c).toLowerCase()), niv: niv
    };
    p.rayon = rayon(p);
    p.richesse = (p.acc.length + p.adj.length) / 40 + (p.image ? 0.01 : 0);
    return p;
  }

  class SobrerieQuizz extends HTMLElement {

    connectedCallback() {
      if (this.monte) return;
      this.monte = true;

      this.cle = 'sobrerie-quizz-' + (this.dataset.sectionId || 'defaut');
      this.reduireAnimations = typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

      this.reglages = this.lireJSON('[data-quizz-reglages]') || {};
      this.produits = [];
      this.caveEtat = 'chargement';

      this.scene = this.querySelector('[data-quizz-scene]');
      this.barre = this.querySelector('[data-quizz-barre]');
      this.jauge = this.querySelector('[data-quizz-jauge]');
      this.compteur = this.querySelector('[data-quizz-compteur]');
      this.annonce = this.querySelector('[data-quizz-annonce]');
      this.introBouton = this.querySelector('[data-quizz-demarrer]');

      this.reponses = {};
      this.position = -1;
      this.emailDonne = false;
      this.restaurer();

      if (this.introBouton) {
        this.introBouton.addEventListener('click', () => this.demarrer());
      }

      /* Le quizz se remet à zéro à chaque ouverture dans l'éditeur de thème,
         pour que la section soit toujours prévisualisée depuis le début. */
      if (window.Shopify && window.Shopify.designMode) {
        this.effacer();
      }

      /* Retour de l'inscription : Shopify renvoie sur cette page avec customer_posted=true. */
      this.inscriptionConfirmee = false;
      try {
        const adresse = new URL(window.location.href);
        if (adresse.searchParams.get('customer_posted') === 'true') {
          this.inscriptionConfirmee = true;
          this.emailDonne = true;
          adresse.searchParams.delete('customer_posted');
          if (window.history && window.history.replaceState) {
            const requete = adresse.searchParams.toString();
            window.history.replaceState(window.history.state, '', adresse.pathname + (requete ? '?' + requete : ''));
          }
        }
      } catch (e) {}

      /* La cave se charge dès l'ouverture : elle est prête bien avant les résultats. */
      this.chargerCave();

      /* Une réponse choisie depuis la bande d'invitation : on la retient et on
         entre directement dans le quizz, le visiteur a déjà commencé. */
      if (this.appliquerChoixExterne()) {
        this.demarrer();
      } else if (this.position >= 0) {
        this.afficher();
      }
    }

    /* ---------------------------------------------------------------- *
     * La cave
     * ---------------------------------------------------------------- */

    chargerCave() {
      const url = this.reglages.json_url;
      if (!url) { this.caveEtat = 'erreur'; return; }
      this.caveEtat = 'chargement';
      chargerVue(url)
        .then((produits) => {
          this.produits = produits.map(normaliserProduit).filter((p) => {
            if (EXCLURE_CATS.indexOf(p.cat) !== -1) return false;
            if (!p.dispo || !p.vid) return false;
            return p.type || p.acc.length || p.rayon === 'lot';
          });
          this.caveEtat = 'ok';
        })
        .catch(() => { this.caveEtat = 'erreur'; })
        .then(() => {
          /* Le visiteur est déjà dans le parcours : on rafraîchit l'écran (résultats, ou options à griser). */
          const etape = this.parcours()[this.position];
          if (etape && etape.ecran !== 'email') this.afficher();
        });
    }

    /* Cherche dans l'arbre la réponse dont le libellé correspond au paramètre
       « choix » de l'adresse. La comparaison ignore la casse et les accents,
       pour ne pas dépendre de la façon dont le libellé a été saisi. */
    appliquerChoixExterne() {
      var demande;
      try {
        demande = new URL(window.location.href).searchParams.get('choix');
      } catch (e) {
        return false;
      }
      if (!demande) return false;

      /* Le paramètre ne sert qu'une fois : on le retire de l'adresse dès qu'il
         est lu. Sans cela, une actualisation en plein parcours relancerait ce
         bloc et remettrait le quizz à zéro sur cette seule réponse. */
      this.oublierChoixExterne();

      const aplatir = (texte) => String(texte)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim();

      const cible = aplatir(demande);
      let groupeTrouve = null;
      let optionTrouvee = null;

      ETAPES.forEach((etape) => {
        etape.groupes.forEach((groupe) => {
          groupe.options.forEach((option) => {
            if (optionTrouvee || aplatir(option.l) !== cible) return;
            groupeTrouve = groupe;
            optionTrouvee = option;
          });
        });
      });

      if (!optionTrouvee) return false;

      /* Départ depuis l'invitation : on repart de ce seul choix, sans
         mélanger une éventuelle session précédente. */
      this.reponses = {};
      this.position = -1;
      this.emailDonne = false;
      if (groupeTrouve.type === 'multiple') {
        this.reponses[groupeTrouve.id] = [optionTrouvee.v];
      } else {
        this.reponses[groupeTrouve.id] = optionTrouvee.v;
      }
      this.enregistrer();
      return true;
    }

    oublierChoixExterne() {
      if (!window.history || !window.history.replaceState) return;
      try {
        const adresse = new URL(window.location.href);
        if (!adresse.searchParams.has('choix')) return;
        adresse.searchParams.delete('choix');
        const requete = adresse.searchParams.toString();
        window.history.replaceState(
          window.history.state,
          '',
          adresse.pathname + (requete ? '?' + requete : '') + adresse.hash
        );
      } catch (e) {}
    }

    lireJSON(selecteur) {
      const noeud = this.querySelector(selecteur);
      if (!noeud) return null;
      try {
        return JSON.parse(noeud.textContent);
      } catch (e) {
        return null;
      }
    }

    /* ---------------------------------------------------------------- *
     * Mémoire de session
     * ---------------------------------------------------------------- */

    restaurer() {
      try {
        const brut = sessionStorage.getItem(this.cle);
        if (!brut) return;
        const etat = JSON.parse(brut);
        this.reponses = etat.reponses || {};
        /* Le rayon était à choix multiple : une session de l'époque garde sa première réponse. */
        if (Array.isArray(this.reponses.famille)) {
          if (this.reponses.famille.length) this.reponses.famille = this.reponses.famille[0];
          else delete this.reponses.famille;
        }
        this.position = typeof etat.position === 'number' ? etat.position : -1;
        this.emailDonne = etat.emailDonne === true;
      } catch (e) {
        /* Navigation privée ou stockage refusé : le quizz repart de zéro. */
      }
    }

    enregistrer() {
      try {
        sessionStorage.setItem(this.cle, JSON.stringify({
          reponses: this.reponses,
          position: this.position,
          emailDonne: this.emailDonne
        }));
      } catch (e) {
        /* Sans mémoire de session le quizz reste utilisable, sans reprise. */
      }
    }

    effacer() {
      this.reponses = {};
      this.position = -1;
      this.emailDonne = false;
      try { sessionStorage.removeItem(this.cle); } catch (e) {}
    }

    /* ---------------------------------------------------------------- *
     * Parcours
     * ---------------------------------------------------------------- */

    /* Les étapes réellement traversées dépendent des réponses déjà données,
       donc la liste et la progression se recalculent à chaque affichage. */
    parcours() {
      const etapes = ETAPES.filter((e) => !e.si || e.si(this.reponses));
      if (this.reglages.demander_email !== false) {
        etapes.push({ id: 'email', ecran: 'email' });
      }
      etapes.push({ id: 'resultats', ecran: 'resultats' });
      return etapes;
    }

    demarrer() {
      this.position = 0;
      this.afficher(true);
    }

    avancer() {
      const etapes = this.parcours();
      const etape = etapes[this.position];
      if (etape && !this.valider(etape)) return;
      if (this.position < etapes.length - 1) {
        this.position += 1;
        this.afficher(true);
      }
    }

    reculer() {
      if (this.position <= 0) return;
      this.position -= 1;
      this.afficher(true);
    }

    allerA(idEtape) {
      const index = this.parcours().findIndex((e) => e.id === idEtape);
      if (index >= 0) {
        this.position = index;
        this.afficher(true);
      }
    }

    valider(etape) {
      if (!etape.groupes) return true;
      let complet = true;
      etape.groupes.forEach((groupe) => {
        const valeur = this.reponses[groupe.id];
        if (groupe.type === 'unique') {
          if (!valeur) complet = false;
        } else {
          const minimum = etape.facultative ? 0 : (groupe.minimum || 0);
          if ((valeur || []).length < minimum) complet = false;
        }
      });
      if (!complet) {
        const alerte = this.querySelector('[data-quizz-alerte]');
        if (alerte) {
          alerte.hidden = false;
          this.annoncer(TEXTES.choisirUn);
        }
      }
      return complet;
    }

    annoncer(texte) {
      if (this.annonce) this.annonce.textContent = texte;
    }

    /* ---------------------------------------------------------------- *
     * Rendu
     * ---------------------------------------------------------------- */

    afficher(deplacerFocus) {
      const etapes = this.parcours();
      if (this.position > etapes.length - 1) this.position = etapes.length - 1;
      const etape = etapes[this.position];
      if (!etape) return;

      this.classList.add('quizz--demarre');
      this.classList.toggle('quizz--sur-resultats', etape.ecran === 'resultats');
      /* Les sections sous le quizz (boutique, etc.) n'apparaissent qu'avec les résultats. */
      document.body.classList.toggle('quizz-resultats-visibles', etape.ecran === 'resultats');
      const intro = this.querySelector('[data-quizz-intro]');
      if (intro) intro.hidden = true;

      if (etape.ecran === 'resultats') {
        this.scene.innerHTML = this.vueResultats();
      } else if (etape.ecran === 'email') {
        this.scene.innerHTML = this.vueEmail();
      } else {
        this.scene.innerHTML = this.vueQuestion(etape, etapes);
      }

      this.majProgression(etapes, etape);
      this.brancher(etape);
      this.enregistrer();
      this.annoncer(etape.titre || this.reglages.titre_resultats || '');

      if (deplacerFocus) {
        const cible = this.scene.querySelector('[data-quizz-focus]');
        if (cible) cible.focus({ preventScroll: true });
        if (etape.ecran === 'resultats') this.cadrerResultats(); else this.cadrer();
      }
    }

    /* À l'arrivée sur les résultats, le titre se cale en haut de la fenêtre : on voit d'un coup
       le titre, le bouton panier et les quatre cartes, sans l'en-tête ni la marge du dessus. */
    cadrerResultats() {
      const ecran = this.scene.querySelector('.quizz__ecran--resultats');
      if (!ecran) { this.cadrer(); return; }
      const cible = window.scrollY + ecran.getBoundingClientRect().top - 28;
      window.scrollTo({ top: Math.max(0, cible), behavior: this.reduireAnimations ? 'auto' : 'smooth' });
    }

    /* Ramène le haut du quizz dans la fenêtre sans sauter brutalement,
       et sans rien faire si tout est déjà visible. */
    cadrer() {
      const rect = this.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < 120) return;
      const cible = window.scrollY + rect.top - 24;
      window.scrollTo({ top: Math.max(0, cible), behavior: this.reduireAnimations ? 'auto' : 'smooth' });
    }

    /* Le nombre d'étapes dépend de la branche, donc il n'est pas connu tant
       que le type de boisson n'est pas choisi. Plutôt que d'afficher un total
       qui changerait sous les yeux du visiteur, on affiche le rang seul, et le
       total dès qu'il est stable. */
    totalProjete(etapes) {
      const famille = this.reponses.famille;
      if (!famille) return null;
      let total = etapes.length;
      /* Les spiritueux ouvrent ensuite exactement une question de plus :
         les cocktails, ou les profils pour la dégustation sur glace. */
      if (estCoche(famille, 'spiritueux') && !this.reponses.usage) total += 1;
      return total;
    }

    majProgression(etapes, etape) {
      const rang = this.position + 1;
      const total = this.totalProjete(etapes);
      const reference = total || etapes.length;

      /* Sur les résultats, la barre de progression n'a plus rien à dire : on la retire. */
      if (this.barre) this.barre.hidden = etape.ecran === 'resultats';
      if (etape.ecran === 'resultats') return;
      if (this.jauge) this.jauge.style.width = Math.round((rang / reference) * 100) + '%';
      if (this.compteur) {
        this.compteur.textContent = total
          ? TEXTES.etape + ' ' + rang + ' ' + TEXTES.sur + ' ' + total
          : TEXTES.etape + ' ' + rang;
      }
    }

    vueQuestion(etape, etapes) {
      const groupes = etape.groupes.map((groupe) => {
        const options = groupe.options.map((option) => this.vueOption(groupe, option)).join('');
        const legende = groupe.label
          ? '<legend class="quizz__legende">' + echapper(groupe.label) + '</legend>'
          : '<legend class="visually-hidden">' + echapper(etape.titre) + '</legend>';
        return '<fieldset class="quizz__groupe">' + legende +
          '<div class="quizz__options quizz__options--' + (etape.taille || 'compact') + '">' + options + '</div>' +
          '</fieldset>';
      }).join('');

      const facultative = etape.facultative || this.optionnelle(etape);

      return '' +
        '<div class="quizz__ecran" data-quizz-ecran="question">' +
          '<p class="quizz__eyebrow">' + echapper(etape.eyebrow || '') + '</p>' +
          '<h2 class="quizz__titre" tabindex="-1" data-quizz-focus>' + echapper(etape.titre) + '</h2>' +
          (etape.aide ? '<p class="quizz__aide">' + echapper(etape.aide) + '</p>' : '') +
          groupes +
          '<p class="quizz__alerte" data-quizz-alerte hidden>' + echapper(TEXTES.choisirUn) + '</p>' +
          '<div class="quizz__nav">' +
            (this.position > 0
              ? '<button type="button" class="quizz__retour" data-quizz-retour>' + echapper(TEXTES.retour) + '</button>'
              : '<span></span>') +
            '<div class="quizz__nav-fin">' +
              (facultative ? '<button type="button" class="quizz__passer" data-quizz-passer>' + echapper(TEXTES.passer) + '</button>' : '') +
              '<button type="button" class="button button--regular button--solid quizz__suivant" data-quizz-suivant>' + echapper(TEXTES.suivant) + '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    }

    optionnelle(etape) {
      return etape.groupes.every((g) => g.type === 'multiple' && !g.minimum);
    }

    /* Vrai si répondre cette option ne laisserait aucune bouteille : on la grise plutôt que de mener au vide.
       Seules les réponses qui filtrent (rayon, usage, style de bière, contrainte) sont concernées. */
    epuisee(groupe, option) {
      if (this.caveEtat !== 'ok' || option.x) return false;
      if (!(option.fam || option.usage || option.style || option.contrainte)) return false;
      const courant = this.reponses[groupe.id];
      let valeurs;
      if (groupe.type === 'multiple') {
        /* On teste l'option seule dans son groupe : « IPA » doit être possible en soi. */
        valeurs = [option.v];
        if (groupe.id === 'contraintes') valeurs = (courant || []).filter((v) => v !== 'aucune').concat(estCoche(courant, option.v) ? [] : [option.v]);
      } else {
        valeurs = option.v;
      }
      return this.compter(groupe.id, valeurs) === 0;
    }

    vueOption(groupe, option) {
      const nom = 'quizz-' + groupe.id;
      const identifiant = nom + '-' + option.v;
      const multiple = groupe.type === 'multiple';
      const valeur = this.reponses[groupe.id];
      const actif = multiple ? estCoche(valeur, option.v) : valeur === option.v;
      const epuisee = !actif && this.epuisee(groupe, option);

      /* Illustration optionnelle : le fichier est déposé dans assets/ et son
         nom indiqué sur la réponse. Sans fichier, la tuile reste au texte. */
      const icone = (option.icone && this.reglages.base_assets)
        ? '<img class="quizz__tuile-icone" src="' + echapper(this.reglages.base_assets + option.icone) +
          '" alt="" aria-hidden="true" loading="lazy" width="36" height="36">'
        : '';

      return '' +
        '<div class="quizz__option">' +
          '<input class="visually-hidden quizz__input" type="' + (multiple ? 'checkbox' : 'radio') + '"' +
            ' name="' + nom + '" id="' + identifiant + '" value="' + echapper(option.v) + '"' +
            (actif ? ' checked' : '') +
            ' data-quizz-champ data-groupe="' + echapper(groupe.id) + '"' +
            ' data-type="' + (multiple ? 'multiple' : 'unique') + '"' +
            (option.x ? ' data-exclusif="true"' : '') +
            (epuisee ? ' disabled' : '') + '>' +
          '<label class="quizz__tuile' + (icone ? ' quizz__tuile--illustree' : '') + (epuisee ? ' quizz__tuile--epuisee' : '') + '" for="' + identifiant + '">' +
            '<span class="quizz__tuile-marque" aria-hidden="true"></span>' +
            icone +
            '<span class="quizz__tuile-texte">' +
              '<span class="quizz__tuile-label">' + echapper(option.l) + (epuisee ? ' <span class="quizz__tuile-epuise">' + echapper(TEXTES.epuise) + '</span>' : '') + '</span>' +
              (option.h ? '<span class="quizz__tuile-aide">' + echapper(option.h) + '</span>' : '') +
            '</span>' +
          '</label>' +
        '</div>';
    }

    vueEmail() {
      const r = this.reglages;
      return '' +
        '<div class="quizz__ecran quizz__ecran--email" data-quizz-ecran="email">' +
          '<p class="quizz__eyebrow">' + echapper(r.eyebrow_email || 'Presque fini') + '</p>' +
          '<h2 class="quizz__titre" tabindex="-1" data-quizz-focus>' + echapper(r.titre_email || 'Où envoyons-nous votre sélection ?') + '</h2>' +
          (r.texte_email ? '<div class="rte quizz__texte">' + r.texte_email + '</div>' : '') +
          '<form class="quizz__form" data-quizz-form novalidate>' +
            '<label class="quizz__champ-label" for="quizz-email">' + echapper(r.label_email || 'Votre adresse email') + '</label>' +
            '<input class="quizz__champ" type="email" id="quizz-email" name="email" autocomplete="email" inputmode="email" placeholder="prenom@exemple.com" required>' +
            '<p class="quizz__erreur" data-quizz-erreur hidden></p>' +
            (r.texte_consentement
              ? '<label class="quizz__consentement"><input type="checkbox" name="consentement" data-quizz-consentement><span>' + echapper(r.texte_consentement) + '</span></label>'
              : '') +
            '<button type="submit" class="button button--regular button--solid quizz__suivant quizz__suivant--large">' +
              echapper(r.bouton_email || 'Recevoir ma sélection') +
            '</button>' +
          '</form>' +
          '<div class="quizz__nav">' +
            '<button type="button" class="quizz__retour" data-quizz-retour>' + echapper(TEXTES.retour) + '</button>' +
            '<button type="button" class="quizz__passer quizz__passer--fort" data-quizz-passer>' +
              echapper(r.bouton_sauter_email || 'Voir mes résultats sans laisser mon email') +
            '</button>' +
          '</div>' +
        '</div>';
    }

    vueResultats() {
      const r = this.reglages;
      const entete = '' +
        '<h2 class="quizz__titre" tabindex="-1" data-quizz-focus>' + echapper(r.titre_resultats || 'Voici ce qu\'on vous conseille') + '</h2>' +
        (r.texte_resultats ? '<div class="rte quizz__texte">' + r.texte_resultats + '</div>' : '');

      if (this.caveEtat === 'chargement') {
        return '<div class="quizz__ecran quizz__ecran--resultats" data-quizz-ecran="resultats">' + entete +
          '<p class="quizz__attente" role="status">' + echapper(TEXTES.chargement) + '</p></div>';
      }
      if (this.caveEtat === 'erreur') {
        return '<div class="quizz__ecran quizz__ecran--resultats" data-quizz-ecran="resultats">' + entete +
          '<p class="quizz__vide" role="alert">' + echapper(TEXTES.erreurCave) + '</p>' +
          '<div class="quizz__fin">' +
            '<button type="button" class="quizz__btn" data-quizz-recharger>' + echapper(TEXTES.reessayer) + '</button>' +
            '<button type="button" class="quizz__lien" data-quizz-recommencer>' + echapper(TEXTES.recommencer) + '</button>' +
          '</div></div>';
      }

      const resultat = this.calculer();
      const selection = resultat.selection;
      if (!selection.length) return this.vueRien();
      const nombre = r.nombre_resultats || 4;
      const cartes = selection.map((p, i) => this.vueProduit(p, i)).join('');
      let note = this.inscriptionConfirmee ? '<p class="quizz__note quizz__note--merci" role="status">' + echapper(TEXTES.inscrit) + '</p>' : '';
      if (resultat.message) {
        note += '<p class="quizz__note quizz__note--elargi" role="status">' + echapper(resultat.message) + '</p>';
      } else if (selection.length < nombre) {
        note += '<p class="quizz__note" role="status">' + echapper(selection.length === 1 ? TEXTES.peuUne : TEXTES.peu.replace('{n}', String(selection.length))) + '</p>';
      }

      const total = selection.reduce((somme, e) => somme + (Number(e.produit.prixNum) || 0), 0);
      const detail = selection.length + (selection.length > 1 ? ' bouteilles' : ' bouteille') +
        (total > 0 && r.money_format ? ' · ' + formaterMontant(total, r.money_format) : '');

      /* Le bouton panier est l'action principale : il précède les cartes. */
      const panier = selection.length > 1
        ? '<div class="quizz__panier">' +
            '<button type="button" class="quizz__btn" data-quizz-ajouter-tout>' + echapper(r.label_ajouter_tout || TEXTES.ajouterTout) + '</button>' +
            '<span class="quizz__panier-detail">' + echapper(detail) + '</span>' +
            '<p class="quizz__erreur quizz__erreur--panier" data-quizz-erreur-panier hidden>' + echapper(TEXTES.erreurPanier) + '</p>' +
          '</div>'
        : '';

      return '' +
        '<div class="quizz__ecran quizz__ecran--resultats" data-quizz-ecran="resultats">' +
          entete +
          note +
          panier +
          '<div class="quizz__resultats quizz__resultats--' + Math.min(selection.length || 1, 6) + '">' + cartes + '</div>' +
          '<div class="quizz__fin">' +
            '<button type="button" class="quizz__lien" data-quizz-recommencer>' + echapper(TEXTES.recommencer) + '</button>' +
            '<span class="quizz__fin-sep" aria-hidden="true">·</span>' +
            '<button type="button" class="quizz__lien" data-quizz-deplier aria-expanded="false" aria-controls="quizz-recap">' + echapper(TEXTES.modifier) + '</button>' +
          '</div>' +
          '<div class="quizz__recap" id="quizz-recap" data-quizz-recap hidden>' +
            '<p class="quizz__recap-label">' + echapper(TEXTES.vousAvezChoisi) + '</p>' +
            this.vueRecapitulatif() +
          '</div>' +
          this.vueCollections() +
        '</div>';
    }

    /* Les collections qui correspondent aux réponses, pour continuer à explorer. */
    vueCollections() {
      const base = this.reglages.collections_url || '/collections';
      const liens = [];
      const vus = {};
      const ajouter = (cle) => {
        const c = COLLECTIONS[cle];
        if (!c || vus[c[0]] || liens.length >= 4) return;
        vus[c[0]] = true;
        liens.push('<a class="quizz__lien" href="' + echapper(base + '/' + c[0]) + '">' + echapper(c[1]) + '</a>');
      };
      const r = this.reponses;
      (r.accord || []).forEach((v) => ajouter('accord:' + v));
      (r.cocktail || []).forEach((v) => ajouter('cocktail:' + v));
      if (r.famille) {
        ajouter('famille:' + r.famille);
        if (r.famille === 'vins' && !estCoche(r.contraintes, 'non-petillant')) ajouter('famille:vins+bulles');
      }
      if (r.quantite === 'plusieurs' || r.destinataire === 'cadeau') ajouter('quantite:plusieurs');
      if (!liens.length) return '';
      return '<p class="quizz__explorer"><span class="quizz__explorer-label">' + echapper(TEXTES.explorer) + '</span>' +
        liens.join('<span class="quizz__fin-sep" aria-hidden="true">·</span>') + '</p>';
    }

    /* Rappel des réponses en une phrase : chaque segment ramène à sa question. */
    vueRecapitulatif() {
      const segments = [];
      ETAPES.forEach((etape) => {
        if (etape.si && !etape.si(this.reponses)) return;
        const morceaux = [];
        etape.groupes.forEach((groupe) => {
          const valeur = this.reponses[groupe.id];
          const choisies = groupe.type === 'multiple' ? (valeur || []) : (valeur ? [valeur] : []);
          choisies.forEach((v) => {
            const option = groupe.options.find((o) => o.v === v);
            if (!option) return;
            let texte = option.l;
            if (groupe.id === 'destinataire') texte = v === 'cadeau' ? 'Pour offrir' : 'Pour vous';
            if (groupe.id === 'quantite') texte = v === 'plusieurs' ? 'plusieurs bouteilles' : 'une seule bouteille';
            morceaux.push(texte);
          });
        });
        /* Une petite pill par réponse, chacune ramène à sa question. */
        morceaux.forEach((m) => {
          segments.push('<button type="button" class="quizz__recap-pill" data-quizz-modifier="' + echapper(etape.id) + '" title="Modifier cette réponse">' + echapper(m) + '</button>');
        });
      });
      if (!segments.length) return '';
      return '<div class="quizz__recap-pills">' + segments.join('') + '</div>';
    }

    /* Même carte que le sommelier (component-sommelier-card.css), avec le score dans la ligne du producteur. */
    vueProduit(entree, index) {
      const p = entree.produit;
      const r = this.reglages;
      const image = p.image
        ? '<img src="' + echapper(p.image) + '" alt="" loading="lazy" width="400" height="400">'
        : '<span class="smlr-card__noimg">' + echapper(p.titre.charAt(0)) + '</span>';
      const infos = [];
      if (p.style) infos.push(p.style);
      if (p.type === 'Effervescent') infos.push('bulles');
      if (p.alcool) infos.push(p.alcool.replace('.', ','));
      const etiquettes = entree.raisons.slice(0, 2).map((t) => '<span class="smlr-tag">' + echapper(t) + '</span>').join('');
      let score = '';
      if (entree.score !== null) {
        score = entree.score > 0
          ? '<span class="smlr-card__score" title="Correspondance avec vos réponses">' + entree.score + ' %</span>'
          : '<span class="smlr-card__score smlr-card__score--autre">' + echapper(TEXTES.autreIdee) + '</span>';
      }

      return '' +
        '<article class="smlr-card" style="--i: ' + Math.min(index || 0, 11) + ';" data-quizz-produit="' + echapper(p.vid) + '">' +
          '<a class="smlr-card__media" href="' + echapper(p.url) + '" tabindex="-1" aria-hidden="true">' + image + '</a>' +
          '<div class="smlr-card__body">' +
            '<div class="smlr-card__head"><span class="smlr-card__vendor">' + echapper(p.vendeur) + '</span>' + score + '</div>' +
            '<h3 class="smlr-card__title"><a href="' + echapper(p.url) + '">' + echapper(titreCourt(p)) + '</a></h3>' +
            '<span class="smlr-card__price">' + echapper(p.prix) + (infos.length ? ' · ' + echapper(infos.join(' · ')) : '') + '</span>' +
            '<div class="smlr-card__why">' + etiquettes + '</div>' +
            '<button type="button" class="smlr-card__add" data-quizz-ajouter="' + echapper(p.vid) + '">' + echapper(r.label_ajouter || TEXTES.ajouter) + '</button>' +
          '</div>' +
        '</article>';
    }

    /* ---------------------------------------------------------------- *
     * Calcul de la sélection
     * ---------------------------------------------------------------- */

    /* Options retenues, à plat, avec le poids de leur question. */
    selections() {
      const retenues = [];
      ETAPES.forEach((etape) => {
        if (etape.si && !etape.si(this.reponses)) return;
        etape.groupes.forEach((groupe) => {
          const valeur = this.reponses[groupe.id];
          const choisies = groupe.type === 'multiple' ? (valeur || []) : (valeur ? [valeur] : []);
          choisies.forEach((v) => {
            const option = groupe.options.find((o) => o.v === v);
            if (option) retenues.push({ groupe: groupe.id, option: option, poids: POIDS[groupe.id] || 0 });
          });
        });
      });
      return retenues;
    }

    /* Vrai si le produit respecte une contrainte alimentaire, strictement. */
    respecte(p, contrainte) {
      const ci = p.ci;
      if (contrainte === 'vegan') return p.reg.indexOf('Vegan') !== -1 && ci.indexOf('vegan') === -1;
      if (contrainte === 'gluten') return ci.indexOf('intolérantes au gluten') === -1;
      if (contrainte === 'enceinte') return p.alcool === '0.0%' && ci.indexOf('enceintes') === -1;
      if (contrainte === 'tranquille') return p.type !== 'Effervescent';
      return true;
    }

    /* Les filtres stricts tirés des réponses. `substitut` permet de tester une réponse
       hypothétique (pour griser une option) : { groupe, valeurs }. */
    parametres(substitut) {
      const r = Object.assign({}, this.reponses);
      if (substitut) r[substitut.groupe] = substitut.valeurs;
      const retenues = [];
      ETAPES.forEach((etape) => {
        if (etape.si && !etape.si(r)) return;
        etape.groupes.forEach((groupe) => {
          const valeur = r[groupe.id];
          const choisies = groupe.type === 'multiple' ? (valeur || []) : (valeur ? [valeur] : []);
          choisies.forEach((v) => {
            const option = groupe.options.find((o) => o.v === v);
            if (option) retenues.push({ groupe: groupe.id, option: option, poids: POIDS[groupe.id] || 0 });
          });
        });
      });
      return {
        familles: retenues.filter((x) => x.option.fam).map((x) => x.option.fam),
        usage: (retenues.find((x) => x.option.usage) || {}).option || null,
        stylesBiere: retenues.filter((x) => x.option.style).map((x) => x.option.style),
        contraintes: retenues.filter((x) => x.option.contrainte).map((x) => x.option.contrainte),
        pointsRegles: retenues.filter((x) => x.option.acc || x.option.adj || x.option.niv || x.option.styleBonus),
        plusieurs: r.quantite === 'plusieurs',
        cadeau: r.destinataire === 'cadeau',
        voisin: null
      };
    }

    filtrer(pm) {
      return this.produits.filter((p) => {
        if (p.rayon === 'lot' && !pm.plusieurs) return false;
        if (pm.familles.length && p.rayon !== 'lot' && pm.familles.indexOf(p.rayon) === -1) return false;
        if (pm.usage && p.rayon === 'spiritueux') {
          if (pm.usage.usage === 'cocktail' && !(p.type === 'Base pour cocktail' || p.cat === 'Cocktail')) return false;
          if (pm.usage.usage === 'glace' && p.cat !== 'Spiritueux') return false;
        }
        if (pm.stylesBiere.length && p.rayon === 'biere' && pm.stylesBiere.indexOf(p.style) === -1) return false;
        for (let i = 0; i < pm.contraintes.length; i++) {
          if (!this.respecte(p, pm.contraintes[i])) return false;
        }
        return true;
      });
    }

    /* Nombre de bouteilles disponibles si l'on répondait `valeurs` à `groupe`, toutes autres réponses égales. */
    compter(groupe, valeurs) {
      if (this.caveEtat !== 'ok') return null;
      return this.filtrer(this.parametres({ groupe: groupe, valeurs: valeurs })).length;
    }

    /* Quand le strict ne donne rien, on élargit d'un cran à la fois, en le disant.
       Les contraintes alimentaires ne sont jamais relâchées. */
    elargir(pm) {
      const essais = [];
      if (pm.stylesBiere.length) {
        essais.push({
          pm: Object.assign({}, pm, { stylesBiere: [] }),
          message: TEXTES.rienStyle.replace('{x}', pm.stylesBiere.join(' ou ')).replace('{y}', TEXTES.toutesBieres)
        });
      }
      if (pm.usage) {
        essais.push({
          pm: Object.assign({}, pm, { stylesBiere: [], usage: null }),
          message: TEXTES.rienUsage.replace('{x}', pm.usage.l.toLowerCase())
        });
      }
      pm.familles.forEach((f) => {
        const v = VOISINS_RAYON[f];
        if (!v) return;
        essais.push({
          pm: Object.assign({}, pm, { stylesBiere: [], usage: null, familles: v.rayons, voisin: v }),
          message: TEXTES.rienRayon.replace('{x}', NOM_RAYON[f] || f).replace('{y}', v.libelle)
        });
      });
      for (let i = 0; i < essais.length; i++) {
        const gardes = this.filtrer(essais[i].pm);
        if (gardes.length) return { pm: essais[i].pm, gardes: gardes, message: essais[i].message };
      }
      return null;
    }

    calculer() {
      const nombre = this.reglages.nombre_resultats || 4;
      const bonus = this.reglages.produits_bonus || [];
      let pm = this.parametres();
      let message = '';

      /* 1. Filtres stricts, puis élargissement si rien ne passe. */
      let gardes = this.filtrer(pm);
      if (!gardes.length) {
        const secours = this.elargir(pm);
        if (secours) { pm = secours.pm; gardes = secours.gardes; message = secours.message; }
      }
      const pointsRegles = pm.pointsRegles.slice();
      if (pm.voisin) pointsRegles.push({ groupe: 'voisin', poids: 2, option: { l: 'Le plus proche', acc: pm.voisin.acc, adj: pm.voisin.adj } });
      const plusieurs = pm.plusieurs, cadeau = pm.cadeau;

      /* 2. Points. */
      const classes = gardes.map((p, rang) => {
        let points = 0;
        let maximum = 0;
        const raisons = [];

        pointsRegles.forEach((x) => {
          const portee = PORTEE[x.groupe];
          if (portee && portee.indexOf(p.rayon) === -1) return;
          maximum += x.poids;
          const o = x.option;
          let gain = 0;

          if (o.acc) {
            if (o.acc.some((a) => p.acc.indexOf(a) !== -1)) gain = 1;
            else {
              const voisins = [];
              o.acc.forEach((a) => (VOISINS[a] || []).forEach((v) => { if (voisins.indexOf(v) === -1) voisins.push(v); }));
              if (voisins.some((v) => p.acc.indexOf(v) !== -1)) gain = 0.5;
            }
          }
          if (o.adj && o.adj.some((a) => p.adj.indexOf(a) !== -1)) gain = Math.max(gain, 1);
          if (o.styleBonus && o.styleBonus.indexOf(p.style) !== -1) gain = Math.max(gain, 1);
          if (o.niv && gain < 1) {
            const val = p.niv[o.niv[0]];
            if (val != null && val >= o.niv[1] && val <= o.niv[2]) gain = Math.max(gain, 0.5);
          }

          if (gain > 0) {
            points += x.poids * gain;
            if (raisons.indexOf(o.l) === -1) raisons.push(o.l);
          }
        });

        /* Sans réponse qui donne des points (« je me laisse guider »), pas de pourcentage. */
        let score = maximum > 0 ? Math.round((points / maximum) * 100) : null;
        if (score !== null && bonus.indexOf(p.handle) !== -1) score = Math.min(99, score + 8);

        return { produit: p, score: score, raisons: raisons, rang: rang };
      });

      const trier = (a, b) => ((b.score || 0) - (a.score || 0)) || (b.produit.richesse - a.produit.richesse) || (a.rang - b.rang);

      /* 3. Plusieurs bouteilles : lots, packs et coffrets d'abord, bouteilles seules en complément. */
      let selection;
      if (plusieurs) {
        const lots = classes.filter((c) => c.produit.rayon === 'lot' || c.produit.coffret).sort((a, b) => {
          if (cadeau && a.produit.coffret !== b.produit.coffret) return a.produit.coffret ? -1 : 1;
          return trier(a, b);
        });
        const seules = classes.filter((c) => !(c.produit.rayon === 'lot' || c.produit.coffret)).sort(trier);
        selection = lots.concat(seules).slice(0, nombre);
      } else {
        selection = classes.sort(trier).slice(0, nombre);
      }

      return { selection: selection, total: classes.length, message: message, elargi: !!message, pm: pm };
    }

    /* Écran résiduel : même élargi, rien. Chaque bouton retire une réponse et montre ce que ça libère. */
    vueRien() {
      const r = this.reponses;
      const boutons = [];
      const ajouter = (libelle, groupe, valeurs) => {
        const n = this.compter(groupe, valeurs);
        if (n) boutons.push('<button type="button" class="quizz__btn quizz__btn--ghost" data-quizz-retirer="' + echapper(groupe) + '" data-quizz-valeurs="' + echapper(JSON.stringify(valeurs)) + '">' +
          echapper(TEXTES.retirer.replace('{x}', libelle)) + ' <span class="quizz__btn-nb">' + n + '</span></button>');
      };
      const option = (groupeId, v) => { let o = null; ETAPES.forEach((e) => e.groupes.forEach((g) => { if (g.id === groupeId) o = g.options.find((x) => x.v === v) || o; })); return o; };
      (r.contraintes || []).forEach((v) => { const o = option('contraintes', v); if (o && o.contrainte) ajouter(o.l, 'contraintes', (r.contraintes || []).filter((x) => x !== v)); });
      (r.biere || []).forEach((v) => { const o = option('biere', v); if (o && o.style) ajouter(o.l, 'biere', ['peu-importe']); });
      if (r.usage) { const o = option('usage', r.usage); if (o) ajouter(o.l, 'usage', null); }
      if (r.quantite === 'une') ajouter('Une seule bouteille', 'quantite', 'plusieurs');
      const pages = this.reglages.root_url || '/';
      return '' +
        '<div class="quizz__ecran quizz__ecran--resultats quizz__ecran--rien" data-quizz-ecran="resultats">' +
          '<h2 class="quizz__titre" tabindex="-1" data-quizz-focus>' + echapper(TEXTES.rienTitre) + '</h2>' +
          '<p class="quizz__texte">' + echapper(TEXTES.rienTexte) + '</p>' +
          (boutons.length ? '<div class="quizz__rien-options">' + boutons.join('') + '</div>' : '') +
          '<div class="quizz__fin">' +
            '<button type="button" class="quizz__lien" data-quizz-recommencer>' + echapper(TEXTES.recommencer) + '</button>' +
            '<span class="quizz__fin-sep" aria-hidden="true">·</span>' +
            '<button type="button" class="quizz__lien" data-quizz-deplier aria-expanded="false" aria-controls="quizz-recap">' + echapper(TEXTES.modifier) + '</button>' +
            '<span class="quizz__fin-sep" aria-hidden="true">·</span>' +
            '<a class="quizz__lien" href="' + echapper(pages + 'pages/sommelier') + '">' + echapper(TEXTES.sommelierLien) + '</a>' +
          '</div>' +
          '<div class="quizz__recap" id="quizz-recap" data-quizz-recap hidden>' +
            '<p class="quizz__recap-label">' + echapper(TEXTES.vousAvezChoisi) + '</p>' +
            this.vueRecapitulatif() +
          '</div>' +
          this.vueCollections() +
        '</div>';
    }

    /* ---------------------------------------------------------------- *
     * Panier
     * ---------------------------------------------------------------- */

    ajouterAuPanier(lignes) {
      const r = this.reglages;
      const url = (r.cart_add_url || '/cart/add') + '.js';
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: lignes.map((id) => ({ id: Number(id), quantity: 1 })) })
      }).then((reponse) => {
        if (!reponse.ok) throw new Error(String(reponse.status));
        return reponse.json();
      }).then((donnees) => {
        if (typeof window.refreshCart === 'function') window.refreshCart(true);
        else if (r.cart_url) window.location.href = r.cart_url;
        return donnees;
      });
    }

    surAjout(bouton, lignes) {
      const erreur = this.scene.querySelector('[data-quizz-erreur-panier]');
      if (erreur) erreur.hidden = true;
      const libelle = bouton.textContent;
      bouton.disabled = true;
      bouton.classList.add('is-loading');
      bouton.textContent = TEXTES.ajoutEnCours;
      this.ajouterAuPanier(lignes)
        .then(() => {
          bouton.classList.remove('is-loading');
          bouton.classList.add('is-done');
          bouton.textContent = TEXTES.ajoute;
          this.annoncer(TEXTES.ajoute);
          window.setTimeout(() => {
            bouton.disabled = false;
            bouton.classList.remove('is-done');
            bouton.textContent = libelle;
          }, 2200);
        })
        .catch(() => {
          bouton.disabled = false;
          bouton.classList.remove('is-loading');
          bouton.textContent = libelle;
          if (erreur) erreur.hidden = false;
          this.annoncer(TEXTES.erreurPanier);
        });
    }

    /* ---------------------------------------------------------------- *
     * Interactions
     * ---------------------------------------------------------------- */

    brancher(etape) {
      this.scene.querySelectorAll('[data-quizz-champ]').forEach((champ) => {
        champ.addEventListener('change', () => this.surChoix(champ, etape));
      });

      const retour = this.scene.querySelector('[data-quizz-retour]');
      if (retour) retour.addEventListener('click', () => this.reculer());

      const suivant = this.scene.querySelector('[data-quizz-suivant]');
      if (suivant) suivant.addEventListener('click', () => this.avancer());

      const passer = this.scene.querySelector('[data-quizz-passer]');
      if (passer) passer.addEventListener('click', () => {
        if (etape.groupes) {
          etape.groupes.forEach((g) => { delete this.reponses[g.id]; });
        }
        const etapes = this.parcours();
        this.position = Math.min(this.position + 1, etapes.length - 1);
        this.afficher(true);
      });

      const recommencer = this.scene.querySelector('[data-quizz-recommencer]');
      if (recommencer) recommencer.addEventListener('click', () => {
        this.effacer();
        this.demarrer();
      });

      const recharger = this.scene.querySelector('[data-quizz-recharger]');
      if (recharger) recharger.addEventListener('click', () => {
        this.chargerCave();
        this.afficher();
      });

      this.scene.querySelectorAll('[data-quizz-modifier]').forEach((bouton) => {
        bouton.addEventListener('click', () => this.allerA(bouton.dataset.quizzModifier));
      });

      this.scene.querySelectorAll('[data-quizz-retirer]').forEach((bouton) => {
        bouton.addEventListener('click', () => {
          const groupe = bouton.dataset.quizzRetirer;
          let valeurs = null;
          try { valeurs = JSON.parse(bouton.dataset.quizzValeurs); } catch (e) {}
          if (valeurs === null) delete this.reponses[groupe]; else this.reponses[groupe] = valeurs;
          if (groupe === 'usage') this.nettoyer();
          this.enregistrer();
          this.afficher(true);
        });
      });

      const deplier = this.scene.querySelector('[data-quizz-deplier]');
      const recap = this.scene.querySelector('[data-quizz-recap]');
      if (deplier && recap) deplier.addEventListener('click', () => {
        const ouvert = recap.hidden;
        recap.hidden = !ouvert;
        deplier.setAttribute('aria-expanded', ouvert ? 'true' : 'false');
        if (ouvert) { const premier = recap.querySelector('[data-quizz-modifier]'); if (premier) premier.focus(); }
      });

      /* Les ancres des textes (« passez à la boutique ») glissent jusqu'à leur section. */
      this.scene.querySelectorAll('a[href^="#"]').forEach((lien) => {
        lien.addEventListener('click', (e) => {
          const id = lien.getAttribute('href').slice(1);
          const cible = id && document.getElementById(id);
          if (!cible) return;
          e.preventDefault();
          const haut = cible.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top: Math.max(0, haut), behavior: this.reduireAnimations ? 'auto' : 'smooth' });
        });
      });

      this.scene.querySelectorAll('[data-quizz-ajouter]').forEach((bouton) => {
        bouton.addEventListener('click', () => this.surAjout(bouton, [bouton.dataset.quizzAjouter]));
      });

      const tout = this.scene.querySelector('[data-quizz-ajouter-tout]');
      if (tout) tout.addEventListener('click', () => {
        const lignes = Array.from(this.scene.querySelectorAll('[data-quizz-produit]')).map((c) => c.dataset.quizzProduit).filter(Boolean);
        if (lignes.length) this.surAjout(tout, lignes);
      });

      const form = this.scene.querySelector('[data-quizz-form]');
      if (form) form.addEventListener('submit', (e) => this.surEmail(e, form));
    }

    surChoix(champ, etape) {
      const groupe = champ.dataset.groupe;
      const alerte = this.scene.querySelector('[data-quizz-alerte]');
      if (alerte) alerte.hidden = true;

      if (champ.dataset.type === 'unique') {
        this.reponses[groupe] = champ.value;
      } else {
        let valeurs = this.reponses[groupe] || [];
        const exclusif = champ.dataset.exclusif === 'true';

        if (champ.checked) {
          /* Une réponse exclusive (« Aucune contrainte », « Surprenez-moi »)
             remplace les autres, et réciproquement. */
          valeurs = exclusif ? [champ.value] : valeurs.filter((v) => !this.estExclusive(etape, groupe, v));
          if (valeurs.indexOf(champ.value) === -1) valeurs.push(champ.value);
        } else {
          valeurs = valeurs.filter((v) => v !== champ.value);
        }
        this.reponses[groupe] = valeurs;
        this.synchroniser(groupe, valeurs);
      }

      /* Une réponse qui change de branche invalide les questions suivantes,
         sinon on garderait des réponses à des questions plus posées. */
      if (groupe === 'famille' || groupe === 'usage') this.nettoyer();

      this.enregistrer();

      /* Sur les questions à choix unique, on enchaîne tout seul : c'est ce qui
         rend le parcours rapide sur mobile. */
      if (this.avancementAutomatique(etape)) {
        window.setTimeout(() => this.avancer(), this.reduireAnimations ? 0 : 260);
      }
    }

    estExclusive(etape, idGroupe, valeur) {
      const groupe = etape.groupes.find((g) => g.id === idGroupe);
      if (!groupe) return false;
      const option = groupe.options.find((o) => o.v === valeur);
      return !!(option && option.x);
    }

    synchroniser(idGroupe, valeurs) {
      this.scene.querySelectorAll('[data-groupe="' + idGroupe + '"]').forEach((champ) => {
        champ.checked = valeurs.indexOf(champ.value) !== -1;
      });
    }

    nettoyer() {
      ETAPES.forEach((etape) => {
        if (etape.si && !etape.si(this.reponses)) {
          etape.groupes.forEach((g) => { delete this.reponses[g.id]; });
        }
      });
    }

    avancementAutomatique(etape) {
      if (!etape.groupes) return false;
      const tousUniques = etape.groupes.every((g) => g.type === 'unique');
      if (!tousUniques) return false;
      return etape.groupes.every((g) => !!this.reponses[g.id]);
    }

    surEmail(evenement, form) {
      evenement.preventDefault();
      const champ = form.querySelector('input[type="email"]');
      const consentement = form.querySelector('[data-quizz-consentement]');
      const erreur = form.querySelector('[data-quizz-erreur]');
      const email = champ.value.trim();
      const valide = email.length > 3 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      const montrer = (texte) => { if (erreur) { erreur.textContent = texte; erreur.hidden = false; } };

      if (!valide) { montrer(TEXTES.emailInvalide); champ.focus(); return; }
      if (consentement && !consentement.checked) { montrer(TEXTES.consentementRequis); consentement.focus(); return; }
      if (erreur) erreur.hidden = true;

      this.emailDonne = true;
      this.dispatchEvent(new CustomEvent('quizz:email', {
        bubbles: true,
        detail: { email: email, reponses: this.reponses }
      }));

      /* Inscription par le vrai formulaire client de Shopify (le même que le pied de page).
         La page se recharge sur les résultats : on avance la position avant de soumettre. */
      const formulaire = this.querySelector('[data-quizz-newsletter]');
      const cible = this.querySelector('[data-quizz-newsletter-email]');
      if (formulaire && cible) {
        const etapes = this.parcours();
        this.position = Math.min(this.position + 1, etapes.length - 1);
        this.enregistrer();
        cible.value = email;
        /* requestSubmit déclenche l'événement submit, que l'hCaptcha de Shopify écoute pour
           ajouter son jeton avant l'envoi ; submit() enverrait le formulaire sans jeton. */
        if (typeof formulaire.requestSubmit === 'function') formulaire.requestSubmit();
        else formulaire.submit();
        return;
      }
      this.avancer();
    }
  }

  if (typeof customElements.get('sobrerie-quizz') === 'undefined') {
    customElements.define('sobrerie-quizz', SobrerieQuizz);
  }
}
